import { ChangeDetectionStrategy, Component, computed, effect, inject, input, SecurityContext, signal } from '@angular/core';
import { UiAlertComponent } from '@big-direkt/ui/alert';
import { EnvironmentService } from '@big-direkt/utils/environment';
import { SanitizePipe } from '@big-direkt/utils/shared';
import { translateObjectSignal } from '@jsverse/transloco';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { type ErrorNodeMessage } from '../models/error-node-message';
import { type ErrorNodeModel } from '../models/error-node-model';
import { ErrorNodeLoaderService } from '../services/error-node-loader.service';
import { ErrorNodeMessageExtractorService } from '../services/error-node-message-extractor.service';

@Component({
    selector: 'big-error-handling-alert',
    templateUrl: './error-handling-alert.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SanitizePipe, UiAlertComponent],
})
export class ErrorHandlingAlertComponent {
    private readonly environmentService = inject(EnvironmentService);
    private readonly errorNodeLoaderService = inject(ErrorNodeLoaderService);
    private readonly errorNodeMessageExtractorService = inject(ErrorNodeMessageExtractorService);

    public readonly phoneNumber = this.environmentService.errorPhoneNumber;
    public readonly securityContext: SecurityContext = SecurityContext.HTML;

    public readonly errorCode = input<number>();
    public readonly errorType = input('general');

    public readonly data = signal<ErrorNodeMessage | undefined>(undefined);
    public readonly translate = translateObjectSignal('errorHandlingAlert', {
        content: {
            number: this.phoneNumber.number,
            title: this.phoneNumber.title,
        },
    });

    public readonly proccesedData = computed<ErrorNodeMessage|undefined>(() => {
        const data = this.data();
        const translate = this.translate();

        if(!data) {
            return undefined;
        }

        return {
            alertType: data.alertType,
            ...(data.title || translate['title'] ? { title: data.title ?? translate['title'] } : {}),
            ...(data.content || translate['content'] ? { content: data.content ?? translate['content'] } : {}),
        };
    });

    public constructor() {
        effect(() => {
            const errorCode = this.errorCode();
            const errorType = this.errorType();

            if (!errorCode) {
                this.data.set(undefined);

                return;
            }

            void firstValueFrom(
                this.errorNodeLoaderService.get(errorCode, errorType).pipe(
                    map((node: ErrorNodeModel) => this.errorNodeMessageExtractorService.extract(node)),
                    tap((data: ErrorNodeMessage) => {
                        this.data.set(data);
                    }),
                    catchError(() => {
                        this.data.set({
                            alertType: 'danger',
                        });

                        return of(undefined);
                    }),
                ),
            );
        });
    }
}
